import { BrowserRouter, Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { ReactLenis } from "lenis/react";
import { Toaster, toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { CurrencyProvider } from "@/lib/currency";
import Home from "@/pages/Home";
import TestimonialsPage from "@/pages/TestimonialsPage";
import JournalArticlePage from "@/pages/JournalArticlePage";
import CommunityPage from "@/pages/CommunityPage";
import ConsultationsPage from "@/pages/ConsultationsPage";
import CaseStudyPage from "@/pages/CaseStudyPage";
import PartnerPage from "@/pages/PartnerPage";
import PaymentCallback from "@/pages/PaymentCallback";
import ProductPage from "@/pages/ProductPage";
import EmergencyPage from "@/pages/EmergencyPage";
import LegalPage from "@/pages/LegalPage";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import JourneyPage from "@/pages/JourneyPage";
import DestinationsPage from "@/pages/DestinationsPage";
import DestinationPage from "@/pages/DestinationPage";
import GuidesPage from "@/pages/GuidesPage";
import ServicesPage from "@/pages/ServicesPage";
import OrderHistoryPage from "@/pages/OrderHistoryPage";
import MembershipPage from "@/pages/MembershipPage";
import MembersPage from "@/pages/MembersPage";
import MemberGuidePage from "@/pages/MemberGuidePage";
import FaqPage from "@/pages/FaqPage";
import ExperiencesPage from "@/pages/ExperiencesPage";
import DettyDecemberPage from "@/pages/DettyDecemberPage";
import ShopPage from "@/pages/ShopPage";
import ConciergePage from "@/pages/ConciergePage";
import AboutPage from "@/pages/AboutPage";
import MembershipsPage from "@/pages/MembershipsPage";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
  }, [pathname]);
  return null;
};

// Emergent-managed Google sign-in: lands back on /members#session_id=... and we exchange it
// for a JÀWÀ member JWT (same token the email/password login issues).
const GoogleAuthCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const sessionId = location.hash.split("session_id=")[1]?.split("&")[0];
    if (!sessionId) {
      navigate("/members", { replace: true });
      return;
    }
    api
      .post("/members/google/session", { session_id: sessionId })
      .then((res) => {
        localStorage.setItem("jawa_member_token", res.data.token);
        navigate("/members", { replace: true });
      })
      .catch(() => {
        toast.error("Google sign-in could not be completed — please try again");
        navigate("/members", { replace: true });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-jawa-ivory" data-testid="google-auth-callback">
      <Loader2 className="h-8 w-8 animate-spin text-jawa-turquoise" />
    </div>
  );
};

const AppRoutes = () => {
  const location = useLocation();
  // Detect the OAuth fragment synchronously during render (prevents race conditions)
  if (location.hash?.includes("session_id=")) {
    return <GoogleAuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/journeys/:journeyId" element={<JourneyPage />} />
      <Route path="/journal" element={<Navigate to="/testimonials" replace />} />
      <Route path="/testimonials" element={<TestimonialsPage />} />
      <Route path="/journal/:slug" element={<JournalArticlePage />} />
      <Route path="/community" element={<CommunityPage />} />
      <Route path="/consultations" element={<ConsultationsPage />} />
      <Route path="/tours" element={<Navigate to="/experiences" replace />} />
      <Route path="/case-study" element={<CaseStudyPage />} />
      <Route path="/partner-with-us" element={<PartnerPage />} />
      <Route path="/payment/callback" element={<PaymentCallback />} />
      <Route path="/shop/p/:productId" element={<ProductPage />} />
      <Route path="/emergency" element={<EmergencyPage />} />
      <Route path="/legal" element={<LegalPage />} />
      <Route path="/destinations" element={<DestinationsPage />} />
      <Route path="/destinations/state/:stateSlugParam" element={<DestinationPage />} />
      <Route path="/destinations/:slug" element={<DestinationPage />} />
      <Route path="/guides" element={<GuidesPage />} />
      <Route path="/services" element={<ServicesPage />} />
      <Route path="/orders" element={<OrderHistoryPage />} />
      <Route path="/membership/:audience" element={<MembershipPage />} />
      <Route path="/members" element={<MembersPage />} />
      <Route path="/members/guides/:audience" element={<MemberGuidePage />} />
      <Route path="/faq" element={<FaqPage />} />
      <Route path="/experiences" element={<ExperiencesPage />} />
      <Route path="/detty-december" element={<DettyDecemberPage />} />
      <Route path="/shop" element={<ShopPage />} />
      <Route path="/concierge" element={<ConciergePage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/memberships" element={<MembershipsPage />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
    </Routes>
  );
};

function App() {
  return (
    <ReactLenis root options={{ lerp: 0.09, anchors: true }}>
      <CurrencyProvider>
        <BrowserRouter>
          <ScrollToTop />
          <AppRoutes />
        </BrowserRouter>
        <Toaster position="bottom-right" toastOptions={{ style: { background: "#2A1F1D", color: "#F9F6F0", border: "1px solid #E5E0D8" } }} />
      </CurrencyProvider>
    </ReactLenis>
  );
}

export default App;
